import React from "react";
import { X, ArrowRight, Info, Sparkles, Layers } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapBottomSheetSurfaceProps } from "../../contracts/map-workspace";

export function MapBottomSheetSurface({
  title,
  subtitle,
  description,
  badges = [],
  summary = [],
  content,
  actions = [],
  onAction,
  onInspect,
  onClear,
  className,
}: MapBottomSheetSurfaceProps) {
  return (
    <div
      className={cn(
        "flex flex-col w-full bg-background border-t border-border rounded-t-2xl shadow-[0_-10px_40px_rgb(0,0,0,0.1)] animate-in fade-in slide-in-from-bottom-8 duration-500",
        className
      )}
    >
      {/* Handle / Accent Area */}
      <div className="flex flex-col items-center pt-3 pb-2 shrink-0">
        <div className="w-12 h-1.5 rounded-full bg-muted/50 border border-border/30" />
      </div>

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Left/Main Column: Identity & Description */}
        <div className="flex flex-col flex-1 p-6 pt-2 overflow-y-auto">
          <div className="flex items-start justify-between">
            <div className="flex flex-col">
              <h2 className="text-lg font-black text-foreground leading-none tracking-tight">
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs font-bold text-primary mt-1 uppercase tracking-widest opacity-80">
                  {subtitle}
                </p>
              )}
            </div>
            {onClear && (
              <button
                onClick={onClear}
                className="p-2 -mt-2 -mr-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors md:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {badges.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {badges.map((badge, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-sm bg-primary/10 text-primary border border-primary/20"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}

          {description && (
            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Info className="w-3.5 h-3.5" />
                <span className="text-[9px] font-black uppercase tracking-[0.2em]">Context</span>
              </div>
              <p className="text-sm text-foreground/70 leading-relaxed max-w-2xl">
                {description}
              </p>
            </div>
          )}

          {content ? (
            <div className="mt-6">
              {content}
            </div>
          ) : null}
        </div>

        {/* Right Column/Panel: Stats & Actions */}
        <div className="flex flex-col w-full md:w-80 border-t md:border-t-0 md:border-l border-border bg-muted/5 shrink-0">
          <div className="p-6 space-y-6">
            {/* Summary List */}
            {summary.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="text-[9px] font-black uppercase tracking-[0.2em]">Summary</span>
                </div>
                <div className="space-y-2">
                  {summary.map((item) => (
                    <div key={item.key} className="flex items-center justify-between text-xs">
                      <span className="font-bold text-muted-foreground">{item.label}</span>
                      <span className="font-mono font-black text-foreground">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 border border-dashed border-border rounded flex items-center justify-center text-[10px] text-muted-foreground font-medium">
                No summary data
              </div>
            )}

            {/* Action Cluster */}
            <div className="flex flex-col gap-2">
              {actions.map((action) => (
                <button
                  key={action.id}
                  disabled={action.disabled}
                  onClick={() => onAction?.(action.id)}
                  className={cn(
                    "flex items-center justify-between w-full h-10 px-4 rounded text-xs font-black transition-all group shadow-sm",
                    "bg-primary text-primary-foreground hover:brightness-110 active:scale-[0.98]",
                    "disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed disabled:active:scale-100"
                  )}
                >
                  <span>{action.label}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              ))}

              {onInspect && (
                <button
                  onClick={onInspect}
                  className="flex items-center justify-center gap-2 w-full h-10 rounded text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-background transition-all border border-transparent hover:border-border"
                >
                  <Layers className="w-3.5 h-3.5 opacity-50" />
                  <span>Inspect Properties</span>
                </button>
              )}
              
              {onClear && (
                <button
                  onClick={onClear}
                  className="hidden md:flex items-center justify-center gap-2 w-full h-10 rounded text-xs font-bold text-muted-foreground hover:text-red-500 hover:bg-red-500/5 transition-all border border-transparent hover:border-red-500/20 mt-2"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Close Surface</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
